import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Email, Info, Person, Save } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import { DadosFornecedorActionsTypes } from "../context/dadosFornecedorActions"
import { useDadosFornecedorContext } from "../context/useDadosFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: {
            maxWidth: "100%",
        },
    },
    header: {
        marginBottom: theme.spacing(4),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(1),
            marginBottom: theme.spacing(3),
        },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.5rem",
        },
        [theme.breakpoints.down("xs")]: {
            fontSize: "1.25rem",
        },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            borderRadius: 15,
            marginBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
        },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.1rem",
        },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
        },
    },
    fieldContainer: {
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(2),
        },
    },
    textField: {
        backgroundColor: "white",
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "& fieldset": {
                borderColor: "#e0e0e0",
            },
            "&:hover fieldset": {
                borderColor: "#4caf50",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#4caf50",
            },
        },
    },
    legend: {
        display: "flex",
        alignItems: "flex-start",
        marginTop: theme.spacing(1),
        color: "#7f8c8d",
        fontSize: "0.875rem",
        lineHeight: 1.4,
        "& svg": {
            marginRight: theme.spacing(0.5),
            fontSize: "1rem",
            marginTop: "2px",
            flexShrink: 0,
        },
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.8rem",
        },
    },
    switchContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(1),
        backgroundColor: "#f8f9fa",
        borderRadius: 12,
        marginTop: theme.spacing(1),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1.5),
        },
    },
    buttonContainer: {
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "center",
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            gap: theme.spacing(1.5),
            marginTop: theme.spacing(3),
        },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb), 0.3)",
        minWidth: "180px",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb), 0.4)",
        },
        [theme.breakpoints.down("sm")]: {
            minWidth: "100%",
            padding: theme.spacing(2, 3),
        },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.2rem",
        },
    },
}))

export function FormularioDadosFornecedor() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { fornecedor, dispatch, salvar, carregando, erroCarregamento } = useDadosFornecedorContext()
    const desabilitado = !!erroCarregamento
    const { enqueueSnackbar } = useSnackbar()
    const [novaSenha, setNovaSenha] = useState("")
    const [confirmarSenha, setConfirmarSenha] = useState("")

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart("Erro ao buscar dados do fornecedor", "error")
    }, [erroCarregamento])

    const handleCampo = (campo: string, valor: string | boolean) => {
        dispatch({
            type: DadosFornecedorActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo: campo as never, valor },
        })
    }

    const handleSalvar = async () => {
        if (!fornecedor.nome.trim()) {
            alertStart("O nome não pode estar vazio", "error")
            return
        }
        if (!fornecedor.email.trim()) {
            alertStart("O e-mail não pode estar vazio", "error")
            return
        }
        if (novaSenha && novaSenha !== confirmarSenha) {
            alertStart("As senhas não coincidem", "error")
            return
        }

        try {
            await salvar(novaSenha || undefined)
            setNovaSenha("")
            setConfirmarSenha("")
            alertStart("Dados salvos com sucesso!", "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao salvar dados", "error")
        }
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box className={classes.header}>
                    <Box>
                        <Typography variant="h4" className={classes.titleText}>
                            Dados do Fornecedor
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Gerencie as informações da sua conta
                        </Typography>
                    </Box>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Person className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Informações Pessoais
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid item xs={12} sm={12} md={6}>
                                <Box className={classes.fieldContainer}>
                                    <Typography
                                        variant="subtitle1"
                                        style={{ marginBottom: 8, fontWeight: 500 }}
                                    >
                                        Nome
                                    </Typography>
                                    <TextField
                                        className={classes.textField}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={fornecedor.nome}
                                        onChange={e => handleCampo("nome", e.target.value)}
                                        disabled={desabilitado}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <Box className={classes.fieldContainer}>
                                    <Typography
                                        variant="subtitle1"
                                        style={{ marginBottom: 8, fontWeight: 500 }}
                                    >
                                        Telefone
                                    </Typography>
                                    <TextField
                                        className={classes.textField}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={fornecedor.telefone}
                                        onChange={e => handleCampo("telefone", e.target.value)}
                                        disabled={desabilitado}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box className={classes.fieldContainer}>
                                    <Typography
                                        variant="subtitle1"
                                        style={{ marginBottom: 8, fontWeight: 500 }}
                                    >
                                        E-mail
                                    </Typography>
                                    <TextField
                                        className={classes.textField}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="email"
                                        value={fornecedor.email}
                                        onChange={e => handleCampo("email", e.target.value)}
                                        disabled={desabilitado}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Email className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Preferências de E-mail
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {[
                            {
                                campo: "receberEmail",
                                label: "Receber e-mails",
                                descricao: "Habilita o recebimento de todos os e-mails",
                            },
                            {
                                campo: "receberEmailPedidoRealizado",
                                label: "Pedido realizado",
                                descricao: "Receber e-mail quando um pedido for realizado",
                            },
                            {
                                campo: "receberEmailPedidoCancelado",
                                label: "Pedido cancelado",
                                descricao: "Receber e-mail quando um pedido for cancelado",
                            },
                            {
                                campo: "receberEmailPedidoExpirado",
                                label: "Pedido expirado",
                                descricao: "Receber e-mail quando um pedido expirar",
                            },
                            {
                                campo: "receberEmailResumoDiario",
                                label: "Resumo diário",
                                descricao: "Receber um resumo diário dos pedidos",
                            },
                        ].map(({ campo, label, descricao }, index, arr) => (
                            <Box key={campo}>
                                <Box className={classes.fieldContainer}>
                                    <Typography
                                        variant="subtitle1"
                                        style={{ marginBottom: 8, fontWeight: 500 }}
                                    >
                                        {label}
                                    </Typography>
                                    <Box className={classes.switchContainer}>
                                        <Typography variant="body2" style={{ color: "#2c3e50" }}>
                                            {fornecedor[campo as keyof typeof fornecedor]
                                                ? "Ativado"
                                                : "Desativado"}
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(
                                                        fornecedor[
                                                            campo as keyof typeof fornecedor
                                                        ]
                                                    )}
                                                    onChange={e =>
                                                        handleCampo(campo, e.target.checked)
                                                    }
                                                    color="primary"
                                                    disabled={desabilitado}
                                                />
                                            }
                                            label=""
                                        />
                                    </Box>
                                    <Typography className={classes.legend}>
                                        <Info />
                                        {descricao}
                                    </Typography>
                                </Box>
                                {index < arr.length - 1 && (
                                    <Divider style={{ margin: "8px 0 16px" }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Person className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Alterar Senha
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid item xs={12} sm={12} md={6}>
                                <Box className={classes.fieldContainer}>
                                    <Typography
                                        variant="subtitle1"
                                        style={{ marginBottom: 8, fontWeight: 500 }}
                                    >
                                        Nova Senha
                                    </Typography>
                                    <TextField
                                        className={classes.textField}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="password"
                                        value={novaSenha}
                                        onChange={e => setNovaSenha(e.target.value)}
                                        placeholder="Deixe em branco para não alterar"
                                        disabled={desabilitado}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <Box className={classes.fieldContainer}>
                                    <Typography
                                        variant="subtitle1"
                                        style={{ marginBottom: 8, fontWeight: 500 }}
                                    >
                                        Confirmar Senha
                                    </Typography>
                                    <TextField
                                        className={classes.textField}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="password"
                                        value={confirmarSenha}
                                        onChange={e => setConfirmarSenha(e.target.value)}
                                        placeholder="Repita a nova senha"
                                        error={novaSenha !== "" && novaSenha !== confirmarSenha}
                                        helperText={
                                            novaSenha !== "" && novaSenha !== confirmarSenha
                                                ? "As senhas não coincidem"
                                                : ""
                                        }
                                        disabled={desabilitado}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Typography className={classes.legend}>
                            <Info />
                            Preencha apenas se desejar alterar a senha atual
                        </Typography>
                    </Box>
                </Paper>

                <Box className={classes.buttonContainer}>
                    <Button
                        className={classes.saveButton}
                        startIcon={!isMobile && <Save />}
                        onClick={handleSalvar}
                        disabled={carregando || desabilitado}
                        size={isMobile ? "large" : "medium"}
                    >
                        {isMobile ? "💾 Salvar Dados" : "Salvar Dados"}
                    </Button>
                </Box>
            </div>
        </div>
    )
}
